import type { FunctionComponent } from 'react'

import { PrimaryButton, SecondaryButton } from '@monthio/design-system'
import { useModal } from '@monthio/react-components'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type {
	ExtraOrdinaryItemGroupType,
	SavingsCapacityExtraordinaryItemDto,
	SavingsCapacityExtraordinaryItemGroupDto,
	SavingsCapacityExtraordinaryTransactionItemDto,
	TransactionOutputApplicantTransactionDto,
} from '../SavingsCapacity.types'

import { ContinueWithoutSelectionModal } from './ContinueWithoutSelectionModal'
import { ExtraordinaryItemsGroupBasePage } from './ExtraordinaryItemsGroupBasePage'
import { ExtraordinaryItemsSelectionSummaryPage } from './ExtraordinaryItemsSelectionSummaryPage'
import { ManualEntryForm } from './ManualEntryForm'
import {
	TransactionListContainer,
	TransactionListItem,
} from './TransactionListItem'

export const CONTINUE_WITHOUT_SELECTION_MODAL_ID =
	'continueWithoutSelectionModal'
interface ExtraordinaryItemsTransactionsSelectorPageProps {
	onNext: (
		savingsCapacityExtraordinaryItemGroup: SavingsCapacityExtraordinaryItemGroupDto,
	) => void
	onPrevious: () => void
	transactions: TransactionOutputApplicantTransactionDto[]
	extraordinaryGroupType: ExtraOrdinaryItemGroupType
	initialItems?: SavingsCapacityExtraordinaryItemGroupDto
}

export const ExtraordinaryItemsGroupPage: FunctionComponent<
	ExtraordinaryItemsTransactionsSelectorPageProps
> = ({
	onNext,
	onPrevious,
	transactions,
	extraordinaryGroupType,
	initialItems,
}) => {
	const { t } = useTranslation()
	const { openModal, closeModal } = useModal()
	const [isAddingManualEntry, setIsAddingManualEntry] = useState(false)
	const [isShowingSummary, setIsShowingSummary] = useState(false)

	const [extraordinaryItemsGroup, setExtraordinaryItemsGroup] =
		useState<SavingsCapacityExtraordinaryItemGroupDto>(
			() =>
				initialItems ?? { type: extraordinaryGroupType, items: [], notes: '' },
		)

	const [searchKey, setSearchKey] = useState<string | null>(null)

	const handleTransactionSelect = useCallback(
		(transactionId: string, selected: boolean) => {
			setExtraordinaryItemsGroup(
				(prev: SavingsCapacityExtraordinaryItemGroupDto) => {
					const newSelected = { ...prev }
					const transaction = transactions.find((t) => t.id === transactionId)
					if (!transaction) return prev

					const transactionDate = new Date(transaction.dateTime.toString())
					if (selected) {
						const newItem: SavingsCapacityExtraordinaryTransactionItemDto = {
							type: 'transaction',
							amount: Math.abs(transaction.amount),
							description: transaction.text,
							categoryId: transaction.category.id,
							categoryName: transaction.category.description,
							transactionId: transaction.id,
							transactionDate,
						}

						newSelected.items.push(newItem)
					} else {
						newSelected.items = newSelected.items.filter(
							(item: SavingsCapacityExtraordinaryItemDto) => {
								if ('transactionId' in item) {
									return item.transactionId !== transaction.id
								}
								return true
							},
						)
					}
					return newSelected
				},
			)
		},
		[transactions],
	)

	const filteredTransactions = useMemo(() => {
		if (!transactions) return []

		transactions.sort((a, b) => a.amount - b.amount)

		const searchTerm = searchKey?.toLowerCase().trim() ?? ''
		if (!searchTerm) return transactions

		return transactions.filter((transaction) =>
			transaction.text.toLowerCase().includes(searchTerm),
		)
	}, [searchKey, transactions])

	const handleContinueWithoutSelection = useCallback(() => {
		onNext({
			type: extraordinaryGroupType,
			items: [],
			notes: '',
		} as SavingsCapacityExtraordinaryItemGroupDto)
		closeModal(CONTINUE_WITHOUT_SELECTION_MODAL_ID)
	}, [closeModal, extraordinaryGroupType, onNext])

	const renderContinueWithoutSelectionModalBody = useCallback(
		() => (
			<ContinueWithoutSelectionModal
				onContinue={handleContinueWithoutSelection}
				text={t('savingsCapacity-continueWithoutSelectionModal-subtitle')}
			/>
		),
		[handleContinueWithoutSelection, t],
	)

	const openContinueWithoutSelectionModal = useCallback(
		(type: ExtraOrdinaryItemGroupType) => {
			openModal({
				id: CONTINUE_WITHOUT_SELECTION_MODAL_ID,
				title: t('savingsCapacity-continueWithoutSelectionModal-title', {
					category: t(
						`savingsCapacity-extraordinaryItemsCategories-category-${type}-title`,
					),
				}),
				responsive: false,
				closable: true,
				bodyRender: renderContinueWithoutSelectionModalBody,
			})
		},
		[openModal, t, renderContinueWithoutSelectionModalBody],
	)

	const handleStartManualEntry = useCallback(() => {
		setIsAddingManualEntry(true)
	}, [])

	const handleEndManualEntry = useCallback(() => {
		setIsAddingManualEntry(false)

		if (transactions.length === 0) {
			onPrevious()
		}
	}, [onPrevious, transactions.length])

	const handleSaveManualEntry = useCallback(
		(extraordinaryItemGroup: SavingsCapacityExtraordinaryItemGroupDto) => {
			onNext(extraordinaryItemGroup)
		},
		[onNext],
	)

	const handleCommentChange = useCallback(
		(notes: string) => {
			onNext({ ...extraordinaryItemsGroup, notes })
		},
		[onNext, extraordinaryItemsGroup],
	)

	useEffect(() => {
		if (transactions.length === 0) {
			setIsAddingManualEntry(true)
		}
	}, [transactions.length])

	const handleOnNext = useCallback(() => {
		if (extraordinaryItemsGroup.items.length === 0) {
			openContinueWithoutSelectionModal(extraordinaryGroupType)
			return
		}

		setIsShowingSummary(true)
	}, [
		extraordinaryGroupType,
		openContinueWithoutSelectionModal,
		extraordinaryItemsGroup.items.length,
	])

	if (isAddingManualEntry) {
		return (
			<ManualEntryForm
				key={extraordinaryGroupType}
				onNext={handleSaveManualEntry}
				onPrevious={handleEndManualEntry}
				type={extraordinaryGroupType}
			/>
		)
	}

	if (isShowingSummary) {
		return (
			<ExtraordinaryItemsSelectionSummaryPage
				extraordinaryItemGroup={extraordinaryItemsGroup}
				onNext={handleCommentChange}
				onPrevious={() => setIsShowingSummary(false)}
				onSelect={handleTransactionSelect}
			/>
		)
	}

	return (
		<ExtraordinaryItemsGroupBasePage
			buttons={[
				<SecondaryButton
					key="secondary-manualAdd"
					disabled={extraordinaryItemsGroup.items.length > 0}
					onClick={handleStartManualEntry}
					size="xl"
				>
					{t(
						'savingsCapacity-extraordinaryItemsTransactions-addManually-button',
					)}
				</SecondaryButton>,
				<PrimaryButton key="primary-next" onClick={handleOnNext} size="xl">
					{t('savingsCapacity-extraordinaryItemsTransactions-next-button')}
				</PrimaryButton>,
			]}
			onGoBackClick={onPrevious}
			category={extraordinaryGroupType}
			title={t('savingsCapacity-extraordinaryItemsTransactions-header')}
			searchValue={searchKey ?? ''}
			onSearchChange={setSearchKey}
		>
			<TransactionListContainer>
				{filteredTransactions?.map((transaction, index) => (
					<TransactionListItem
						key={index}
						transactionId={transaction.id}
						text={transaction.text}
						date={transaction.dateTime.toString()}
						amount={transaction.amount}
						isSelected={extraordinaryItemsGroup.items.some(
							(item: SavingsCapacityExtraordinaryItemDto) =>
								'transactionId' in item &&
								item.transactionId === transaction.id,
						)}
						onSelect={handleTransactionSelect}
					/>
				))}
			</TransactionListContainer>
		</ExtraordinaryItemsGroupBasePage>
	)
}
